import { formatHours } from "@activecollab/components";
import moment from "moment";
import { createSelector } from "reselect";
import { getFilterProjectTimeConfigOption } from "~/store/selectors/selector.configOptions";
import Angie from "~/utils/angie.jsx";
import { filterByDate } from "~/utils/date.jsx";
import { hoursFormatted, descSortCompare, decimalToMinutes } from "~/utils/utils.jsx";
import { getAllTasksByProjectId, getTaskById } from "./selector.tasks.jsx";

export const getTimeRecords = (state) => state.time_records;
export const getTimeRecordById = (state, id) => {
  return state.time_records[id];
};

export const getAllTimeRecords = createSelector(getTimeRecords, (time_records) => Object.values(time_records));

export const makeGetAllTimeRecordsSortedByRecordDate = () =>
  createSelector(getAllTimeRecords, (timeRecords) =>
    timeRecords.sort((a, b) => {
      let comparator1 = moment(a.record_date).utc();
      let comparator2 = moment(b.record_date).utc();

      if (a.record_date === b.record_date) {
        return a.created_on > b.created_on ? -1 : 1;
      }
      return comparator1 > comparator2 ? -1 : 1;
    })
  );

export const makeGetByParentIdAndParentType = () =>
  createSelector(
    makeGetAllTimeRecordsSortedByRecordDate(),
    (state, parentType) => parentType,
    (state, parentType, parentId) => parentId,
    (timeRecords, parentType, parentId) =>
      timeRecords.filter(
        (record) => record.parent_type === parentType && record.parent_id === parentId && !record.is_trashed
      )
  );

export const calculateAllTrackingForParentTypeAndId = () =>
  createSelector(makeGetByParentIdAndParentType(), (result) => {
    let sum = 0;
    result.forEach((record) => {
      sum += record.value;
    });
    return formatHours(sum);
  });

export const createViewUrlForParentType = createSelector(
  (state, parentType, parentId) => {
    return parentType === `Task`
      ? `${Angie.root_url}/projects/${getTaskById(state, parentId).get(`project_id`)}/tasks/${parentId}`
      : `${Angie.root_url}/projects/${parentId}`;
  },
  (viewUrl) => viewUrl
);

export const getTimeRecordsBetweenDates = createSelector(
  getTimeRecords,
  (state, { startTime }) => startTime,
  (state, { endTime }) => endTime,
  (timeRecords, startTime, endTime) => {
    return Object.keys(timeRecords).reduce((acc, key) => {
      const record = timeRecords[key];
      if (!record.is_trashed && startTime <= record.record_date && record.record_date <= endTime) {
        acc.push({
          id: record.id,
          value: record.value,
          recordDate: record.record_date,
          userId: record.user_id,
          parentType: record.parent_type,
          parentId: record.parent_id,
          summary: record.summary,
          jobTypeId: record.job_type_id,
          billableStatus: record.billable_status,
          createdOn: record.created_on,
        });
      }
      return acc;
    }, []);
  }
);

export const getTimeRecordsBetweenDatesForUser = createSelector(
  getTimeRecordsBetweenDates,
  (state, dates, userId) => userId,
  (timeRecords, userId) => timeRecords?.filter((r) => r?.userId === userId)
);

export const getFilteredProjectTimeRecordsForList = createSelector(
  getAllTimeRecords,
  (state, projectId) => projectId,
  (state, projectId, minDate) => minDate,
  getFilterProjectTimeConfigOption,
  getAllTasksByProjectId,
  (timeRecords, projectId, minDate, allFilters, tasks) => {
    const filters = allFilters[projectId] ?? {};
    return timeRecords
      .filter((timeRecord) => {
        if (timeRecord.project_id !== projectId || timeRecord.is_trashed) {
          return false;
        }

        if (minDate && timeRecord.record_date <= minDate) {
          return false;
        }

        if (filters.status && !filters.status.includes(timeRecord.billable_status)) {
          return false;
        }

        if (filters.location && !filters.location.includes(timeRecord.parent_type)) {
          return false;
        }

        if (filters.job && !filters.job.includes(timeRecord.job_type_id)) {
          return false;
        }

        if (filters.user && !filters.user.includes(timeRecord.user_id)) {
          return false;
        }

        if (filters.date && !filterByDate(timeRecord.record_date, filters.date)) {
          return false;
        }

        if (timeRecord.parent_type === "Task" && !tasks.get(`${timeRecord.parent_id}`)) {
          return false;
        }

        return true;
      })
      .sort((a, b) => {
        if (a.record_date === b.record_date) {
          return a.id > b.id ? -1 : 1;
        } else {
          return descSortCompare(a.record_date, b.record_date);
        }
      });
  }
);

export const getTimeRecordsSumByIds = createSelector(
  getAllTimeRecords,
  (state, ids) => ids,
  (timeRecords, ids) => {
    const selectedRecords = timeRecords.filter((record) => ids.includes(record.id));
    const sumOfValues = selectedRecords.reduce((sum, record) => {
      return sum + decimalToMinutes(record.value);
    }, 0);
    return hoursFormatted({ hours: 0, minutes: sumOfValues }, false, true);
  }
);
